import { AuthenticationServiceProvider } from '../authentication-service/authentication-service';
import { BehaviorSubject } from 'rxjs';
import { ConfigServiceProvider } from '../config-service/config-service';
import { DisplayServiceProvider } from '../display-service/display-service';
import { Events } from 'ionic-angular';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LayoutServiceProvider } from '../layout-service/layout-service';
import { MapOfNumberTo } from '../../../ranking/interfaces/utils';
import { RankingServiceProvider } from '../../../ranking/services/ranking-service/ranking-service';
import { RankingWithKpiMapAndExtras } from '../../../ranking/services/ranking-service/ranking.interface';
import { StorageServiceProvider } from '../storage-service/storage-service';
import { TextServiceProvider } from '../text-service/text-service';
import { TrackJS } from 'trackjs';
import { User } from '../users-service/interfaces/user.interface';
import { UsersServiceProvider } from '../users-service/users-service';

@Injectable()
export class PlayerServiceProvider {
  // todo: change to private and replace all calls with getters
  public player: User = {} as User;
  public account: Account = {} as Account;
  public index: number;
  public tmpName: string;
  public photoUrl: string;
  // todo: type for playerPhoto
  public playerPhoto: any;
  public player$: BehaviorSubject<User>;

  constructor(
    private authenticationService: AuthenticationServiceProvider,
    private configService: ConfigServiceProvider,
    private displayService: DisplayServiceProvider,
    private events: Events,
    private layoutService: LayoutServiceProvider,
    private rankingService: RankingServiceProvider,
    private storageService: StorageServiceProvider,
    private usersService: UsersServiceProvider,
    public http: HttpClient,
    public textService: TextServiceProvider,
  ) {
    this.player$ = new BehaviorSubject({} as User);
  }

  public async load() {
    try {
      return await Promise.all([this.loadPlayer(), this.loadAccount()]);
    } catch (err) {
      return console.error(err);
    }
  }

  public loadPlayer() {
    return new Promise((resolve, reject) => {
      this.http
        .get<User>(this.configService.getUrl('player'))
        .toPromise()
        .then((response: User) => {
          this.storageService.setObject('player', response);
          // todo: make sure it doesn't blow up if there's no params
          this.displayService.setScreenType(response.params.type);
          this.layoutService.setLayoutType(response.params.type);
          this.textService.language = response.langKey;
          this.player = response;
          this.player$.next(response);
          resolve(response);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  }

  public loadAccount() {
    return new Promise((resolve, reject) => {
      this.http
        .get<Account>(this.configService.getUrl('account'))
        .toPromise()
        .then((response: Account) => {
          this.account = response;
          this.storageService.setObject('account', this.account.authorities);
          TrackJS.configure({ userId: this.account.login || '' });
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  }

  getPlayerRankings(): Promise<MapOfNumberTo<RankingWithKpiMapAndExtras[]>> {
    let playerId: number;
    let playerRankings: MapOfNumberTo<RankingWithKpiMapAndExtras[]> = {};
    return new Promise((resolve, reject) =>
      this.storageService
        .getObject('player')
        .then((player: User) => {
          playerId = player.id;
          return this.rankingService.getRankings();
        })
        .then((rankings: MapOfNumberTo<MapOfNumberTo<RankingWithKpiMapAndExtras[]>>) => {
          for (let ranking in rankings) {
            playerRankings[ranking] = rankings[ranking][playerId];
          }
          resolve(playerRankings);
        })
        .catch((error) => reject(error)),
    );
  }

  public changePassword(oldPassword: string, newPassword: string) {
    let params = new URLSearchParams();
    params.append('oldPassword', oldPassword);
    params.append('newPassword', newPassword);

    return this.sendPasswordChangeRequest(params);
  }

  public changePhoto(image: string) {
    return new Promise<void>((resolve, reject) => {
      this.authenticationService
        .getAccessToken()
        .then((token) => {
          fetch(image)
            .then((response) => response.blob())
            .then((blob) => this.sendPhotoBlob(blob, token))
            .then(() => resolve())
            .catch((error) => reject(error));
        })
        .catch((err) => reject(err));
    });
  }

  private sendPhotoBlob(blob: Blob, token: string) {
    const formData = new FormData();
    const file = this.blobToFile(blob, 'profile-photo.jpeg');
    formData.append('photo', file);
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    return this.http
      .post(this.configService.getUrl('photoChange'), formData, { headers })
      .toPromise()
      .then(
        () => Promise.all([this.load(), this.usersService.load()]).then(() => this.events.publish('player:photoChanged')),
        (error) => {
          throw error;
        },
      );
  }

  // cordova-plugin-file overrides the default file, breaking image upload on android, so we need to do this:
  private blobToFile = (blob: Blob, fileName: string): File => {
    var b: any = new Blob([blob], { type: 'multipart/form-data' });
    b.lastModifiedDate = new Date();
    b.name = fileName;
    return <File>blob;
  };

  public deletePhoto(): Promise<any> {
    return new Promise<void>((resolve, reject) => {
      this.http
        .delete(this.configService.getUrl('photoChange'))
        .toPromise()
        .then(() => {
          Promise.all([this.load(), this.usersService.load()]).then(() => {
            this.events.publish('player:photoChanged');
            resolve();
          });
        })
        .catch((err) => {
          reject();
          console.error(err);
        });
    });
  }

  public getPlayer(): Promise<User> {
    return this.storageService.getObject('player') as Promise<User>;
  }

  public getPlayerProperty(): User {
    return this.player ? this.player : null;
  }

  getAccountRoles(): Promise<string[]> {
    return this.storageService.getObject('account') as Promise<string[]>;
  }

  private sendPasswordChangeRequest(params: URLSearchParams) {
    return this.http
      .post(this.configService.getUrl('passwordChange'), params.toString(), {
        headers: {
          'Content-type': 'application/x-www-form-urlencoded; charset=utf-8',
        },
      })
      .toPromise();
  }
}

export interface Account {
  login: string;
  firstName: string;
  lastName: string;
  email: string;
  activated: boolean;
  langKey: string;
  authorities: string[];
}



// WEBPACK FOOTER //
// ./src/modules/core/services/player-service/player-service.ts